// ============================================================================
// PageJumpDropdown.js  —  "Go to page" dropdown for the PDF viewer
// ----------------------------------------------------------------------------
// NOTE: This is an OPTIONAL feature that may be reverted.
//   TO REVERT THIS FEATURE COMPLETELY:
//     1. In Navbar.js, restore the old inline Page Jump block
//        (the `{showPageJump ? (...input...) : (...button...)}` JSX) where the
//        <PageJumpDropdown ... /> tag now sits, and remove the import line.
//     2. Delete this file.
//   Nothing else depends on this component.
// ----------------------------------------------------------------------------
// WHAT IT DOES (hybrid, per requirement — user often has no time to type):
//   • If the PDF has <= PAGE_LIST_THRESHOLD pages  -> show a SIMPLE dropdown
//     list of every page. User just clicks a page. No typing needed.
//   • If the PDF has  > PAGE_LIST_THRESHOLD pages   -> show a SCROLLABLE list
//     PLUS a type-to-filter box at the top. The user can still just scroll &
//     click (no typing required), OR type a number to jump fast.
//
// It reuses the viewer's existing imperative handle (via pdfRef):
//     getTotalPages()      -> total page count of the current PDF
//     getCurrentPageNum()  -> page currently in view
//     scrollToPage(n)      -> actually performs the jump
// ============================================================================

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

// Below this page count we show the plain click-only list; above it we add the
// type-to-filter box + scroller. Tweak freely (e.g. 15 or 20) if 10 feels low.
const PAGE_LIST_THRESHOLD = 10;

// Safety cap: never render more than this many <option> rows at once, so a
// huge PDF (1000+ pages) stays smooth. Typing in the filter narrows it down.
const MAX_RENDERED_ROWS = 200;

export default function PageJumpDropdown({ pdfRef, icon, show, setShow }) {
    // Wrapper around the trigger button + the floating panel — used for
    // anchoring the dropdown and for outside-click detection.
    const wrapperRef = useRef(null);
    const filterInputRef = useRef(null);
    const currentItemRef = useRef(null);

    // The text typed into the filter box (only used in "large PDF" mode).
    const [filter, setFilter] = useState("");

    // Read live values from the viewer each time the dropdown opens.
    const totalPages = pdfRef.current?.getTotalPages?.() || 0;
    const currentPage = pdfRef.current?.getCurrentPageNum?.() || 1;

    // Whether we are in the simple (click-only) layout or the filter+scroll one.
    const isSimpleMode = totalPages > 0 && totalPages <= PAGE_LIST_THRESHOLD;

    // ── Perform the jump, then close the dropdown ──────────────────────────
    const jumpTo = useCallback((pageNum) => {
        const total = pdfRef.current?.getTotalPages?.() || 0;
        // Clamp to a valid page (ignore garbage / out-of-range input).
        if (!pageNum || isNaN(pageNum)) return;
        const clamped = Math.max(1, total ? Math.min(pageNum, total) : pageNum);
        pdfRef.current?.scrollToPage(clamped);
        setShow(false);
        setFilter("");
    }, [pdfRef, setShow]);

    // ── Build the list of page numbers to show (filtered for large PDFs) ───
    const visiblePages = useMemo(() => {
        if (totalPages <= 0) return [];
        const all = Array.from({ length: totalPages }, (_, i) => i + 1);
        const q = filter.trim();
        if (!q) return all.slice(0, MAX_RENDERED_ROWS);
        // Filter: keep pages whose number contains the typed digits.
        return all.filter(n => String(n).includes(q)).slice(0, MAX_RENDERED_ROWS);
    }, [totalPages, filter]);

    // ── Close on outside click / Escape while open ─────────────────────────
    useEffect(() => {
        if (!show) return;
        const onDown = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShow(false);
            }
        };
        const onKey = (e) => { if (e.key === "Escape") setShow(false); };
        // Delay so the click that opened the panel doesn't immediately close it.
        const t = setTimeout(() => {
            document.addEventListener("pointerdown", onDown);
            document.addEventListener("keydown", onKey);
        }, 0);
        return () => {
            clearTimeout(t);
            document.removeEventListener("pointerdown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [show, setShow]);

    // ── When opened: reset filter, focus the box (large mode), and scroll the
    //    current page into view so the user instantly sees where they are. ──
    useEffect(() => {
        if (!show) return;
        setFilter("");
        const t = setTimeout(() => {
            if (!isSimpleMode) filterInputRef.current?.focus();
            currentItemRef.current?.scrollIntoView({ block: "center" });
        }, 30);
        return () => clearTimeout(t);
    }, [show, isSimpleMode]);

    // ── Shared row style for a page entry ──────────────────────────────────
    const rowStyle = (isCurrent) => ({
        padding: "7px 12px",
        cursor: "pointer",
        fontSize: 13,
        fontWeight: isCurrent ? 700 : 500,
        color: isCurrent ? "#1d4ed8" : "#374151",
        background: isCurrent ? "#eff6ff" : "transparent",
        borderBottom: "1px solid #f3f4f6",
        whiteSpace: "nowrap",
    });

    return (
        <div ref={wrapperRef} style={{ position: "relative", display: "inline-flex" }}>
            {/* Trigger button — same look as the other toolbar tool buttons */}
            <button
                className={`tool-btn ${show ? "active" : ""}`}
                onClick={() => setShow(!show)}
                title="Jump to Page"
            >{icon}</button>

            {/* Floating dropdown panel */}
            {show && (
                <div
                    className="tool-popover"
                    style={{
                        position: "absolute", top: "calc(100% + 6px)", left: 0,
                        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                        boxShadow: "0 6px 24px rgba(0,0,0,0.15)", zIndex: 1000,
                        minWidth: 150, overflow: "hidden",
                    }}
                >
                    {/* Header: shows "Page X / total" so the user always sees totals */}
                    <div style={{
                        padding: "7px 12px", fontSize: 11, fontWeight: 700, color: "#6b7280",
                        background: "#f9fafb", borderBottom: "1px solid #eee",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                        <span>Go to page</span>
                        <span style={{ color: "#9ca3af" }}>
                            {currentPage} / {totalPages || "?"}
                        </span>
                    </div>

                    {/* ── LARGE-PDF MODE: type-to-filter box (optional for the user) ── */}
                    {!isSimpleMode && totalPages > 0 && (
                        <div style={{ padding: 6, borderBottom: "1px solid #f0f0f0", background: "#fff" }}>
                            <input
                                ref={filterInputRef}
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value.replace(/[^0-9]/g, ""))}
                                onKeyDown={(e) => {
                                    // Enter = jump straight to the typed number.
                                    if (e.key === "Enter") jumpTo(parseInt(filter, 10));
                                }}
                                placeholder={`Type page (1–${totalPages})…`}
                                style={{
                                    width: "100%", boxSizing: "border-box",
                                    fontSize: 13, padding: "5px 8px",
                                    border: "1px solid #bfdbfe", borderRadius: 6,
                                    outline: "none", background: "#f8fafc", color: "#1d4ed8",
                                }}
                            />
                        </div>
                    )}

                    {/* ── PAGE LIST (used by BOTH modes — scroll & click) ── */}
                    {totalPages > 0 ? (
                        <div style={{ maxHeight: 260, overflowY: "auto" }}>
                            {visiblePages.length === 0 ? (
                                <div style={{ padding: "12px", fontSize: 12, color: "#9ca3af", textAlign: "center" }}>
                                    No matching page
                                </div>
                            ) : (
                                visiblePages.map((n) => {
                                    const isCurrent = n === currentPage;
                                    return (
                                        <div
                                            key={n}
                                            ref={isCurrent ? currentItemRef : null}
                                            onClick={() => jumpTo(n)}
                                            style={rowStyle(isCurrent)}
                                            onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = "#f3f4f6"; }}
                                            onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = "transparent"; }}
                                        >
                                            Page {n}{isCurrent ? "  (current)" : ""}
                                        </div>
                                    );
                                })
                            )}
                            {/* Hint shown when the list was capped for performance */}
                            {visiblePages.length === MAX_RENDERED_ROWS && (
                                <div style={{ padding: "6px 12px", fontSize: 10, color: "#9ca3af", textAlign: "center", background: "#fafafa" }}>
                                    Showing first {MAX_RENDERED_ROWS} — type to narrow
                                </div>
                            )}
                        </div>
                    ) : (
                        // PDF not loaded yet — nothing to list.
                        <div style={{ padding: "12px", fontSize: 12, color: "#9ca3af", textAlign: "center" }}>
                            Loading pages…
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
